import styled from "styled-components";
import PaginatedTable from "../../Componentes/Utils/PaginatedTable";
import { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import { useModal } from "../../contexts/ModalContext";
import { RiRefreshLine, RiNotification3Fill } from "react-icons/ri";
import ModalSelecionarNf from "./ModalSelecionarNf";

const Container = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    width: 100%;
    height: 50px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    border-bottom: 1.2px solid #d3d3d3;
    box-sizing: border-box;
`;

const Title = styled.p`
    color: #333;
    font-size: 16px;
    font-weight: bold;
    margin: auto 0 auto 15px;
`;

const Subtitle = styled.span`
    color: #666;
    font-size: 13px;
    font-weight: normal;
    margin-left: 10px;
`;

const ActionsWrapper = styled.div`
    height: 30px;
    margin-left: auto;
    margin-right: 15px;
    display: flex;
    gap: 10px;
    align-items: center;

    & button {
        height: 100%;
        padding: 0 15px;
        border: 1.2px solid #d3d3d3;
        border-radius: 4px;
        background-color: transparent;
        display: flex;
        align-items: center;
        cursor: pointer;
    }

    & button:hover {
        background-color: #f0f0f0;
    }
`;

const RefreshIcon = styled(RiRefreshLine)`
    width: 15px;
    height: 15px;
    color: #333;
    margin-right: 8px;
    animation: ${(props) => (props.$loading ? "spin 1s linear infinite" : "none")};

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

const Body = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: #fff;
    margin-top: 5px;
    border-radius: 8px;
    border: 1.2px solid #d3d3d3;
    box-sizing: border-box;
`;

const LinhaTabela = styled.tr`
    background-color: ${(props) => (props.$destaque ? "#ecfdf5" : "transparent")};
    cursor: ${(props) => (props.$clicavel ? "pointer" : "default")};

    &:hover {
        background-color: ${(props) => (props.$clicavel ? "#d1fae5" : props.$destaque ? "#ecfdf5" : "transparent")};
    }
`;

const NfDisponivelBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    background-color: #d1fae5;
    color: #065f46;

    & svg {
        width: 14px;
        height: 14px;
    }
`;

const SemNf = styled.span`
    color: #9ca3af;
    font-size: 13px;
`;

const ParcialBadge = styled.span`
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    background-color: #fef9c3;
    color: #854d0e;
`;

const NfsPendentes = () => {
    const api = useApi();
    const { isModalOpen, openModal, closeModal } = useModal();

    const [pagina, setPagina] = useState(1);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const [dados, setDados] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [periodo, setPeriodo] = useState({ inicio: "", fim: "" });

    const [detalhes, setDetalhes] = useState(null);
    const [loadingDetalhes, setLoadingDetalhes] = useState(false);

    const fetchDados = async (page = pagina) => {
        setLoading(true);

        try {
            const params = new URLSearchParams();
            params.append("page", page);
            params.append("limit", 50);
            params.append("apenas_sem_nf", "1");

            const { data } = await api.get(
                `Painel/API/Controladoria/PedidosCompra/VinculoNf/index.php?${params.toString()}`
            );

            setDados(data.data || []);
            setTotalItems(data.quantidade || 0);
            setTotalPages(data.total_pages || 0);
            setPeriodo({
                inicio: data.periodo_inicio || "",
                fim: data.periodo_fim || "",
            });
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDados(pagina);
    }, [pagina]);

    const refreshData = async () => {
        if (refreshing) return;

        setRefreshing(true);
        await fetchDados(pagina);
        setRefreshing(false);
    };

    const formatarPeriodo = () => {
        if (!periodo.inicio || !periodo.fim) return "";
        const fmt = (iso) => iso.split("-").reverse().join("/");
        return `Últimos 30 dias (${fmt(periodo.inicio)} a ${fmt(periodo.fim)})`;
    };

    const abrirNfsDisponiveis = async (item) => {
        if (!item.TEM_NF_DISPONIVEL) return;

        setDetalhes(null);
        setLoadingDetalhes(true);
        openModal("controladoria-selecionar-nf");

        try {
            const params = new URLSearchParams();
            params.append("filial", item.FILIAL);
            params.append("num_pedido", item.NUM_PEDIDO);

            const { data } = await api.get(
                `Painel/API/Controladoria/PedidosCompra/VinculoNf/Detalhes/index.php?${params.toString()}`
            );

            setDetalhes(data);
        } catch (e) {
            console.log(e);
        } finally {
            setLoadingDetalhes(false);
        }
    };

    return (
        <Container>
            <Header>
                <Title>
                    NFs Pendentes
                    <Subtitle>{formatarPeriodo()}</Subtitle>
                </Title>
                <ActionsWrapper>
                    <button onClick={refreshData}>
                        <RefreshIcon $loading={refreshing} />
                        Atualizar
                    </button>
                </ActionsWrapper>
            </Header>

            <Body>
                <PaginatedTable
                    pagina={pagina}
                    setPagina={setPagina}
                    loading={loading}
                    colunas={6}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    thead={
                        <tr>
                            <th>FILIAL</th>
                            <th>NÚMERO PEDIDO</th>
                            <th>DATA EMISSÃO</th>
                            <th>FORNECEDOR</th>
                            <th>USUÁRIO</th>
                            <th>NF DISPONÍVEL</th>
                        </tr>
                    }
                >
                    <tbody>
                        {dados.map((item) => (
                            <LinhaTabela
                                key={`${item.FILIAL}-${item.NUM_PEDIDO}-${item.DT_EMISSAO}`}
                                $destaque={item.TEM_NF_DISPONIVEL}
                                $clicavel={item.TEM_NF_DISPONIVEL}
                                onClick={() => abrirNfsDisponiveis(item)}
                            >
                                <td>{item.FILIAL}</td>
                                <td>
                                    {item.NUM_PEDIDO}
                                    {item.STATUS_PC === "PARCIAL" && <ParcialBadge>Parcial</ParcialBadge>}
                                </td>
                                <td>{item.DT_EMISSAO}</td>
                                <td>{item.FORNECEDOR || " - "}</td>
                                <td>{item.USUARIO || " - "}</td>
                                <td>
                                    {item.TEM_NF_DISPONIVEL ? (
                                        <NfDisponivelBadge
                                            title={
                                                item.DT_NF_DISPONIVEL
                                                    ? `NF emitida em ${item.DT_NF_DISPONIVEL}`
                                                    : "NF disponível para vínculo"
                                            }
                                        >
                                            <RiNotification3Fill />
                                            NF disponível
                                            {item.DT_NF_DISPONIVEL ? ` (${item.DT_NF_DISPONIVEL})` : ""}
                                        </NfDisponivelBadge>
                                    ) : (
                                        <SemNf>Nenhuma NF encontrada</SemNf>
                                    )}
                                </td>
                            </LinhaTabela>
                        ))}
                    </tbody>
                </PaginatedTable>
            </Body>

            {isModalOpen("controladoria-selecionar-nf") && (
                <ModalSelecionarNf
                    fecharModal={closeModal}
                    detalhes={detalhes}
                    loading={loadingDetalhes}
                    onVinculado={() => fetchDados(pagina)}
                />
            )}
        </Container>
    );
};

export default NfsPendentes;
